import React, { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Grid, Typography } from "@mui/material";

import API from "../../api";
import notify from "../Notification/helper";
import { chooseInputComponent } from "../../common/utils/components";
import { getTranslation } from "../../utils/translate";
import { useNavigate } from "react-router-dom";

const BarcodeForm = ({
  beefCutId,
  savedData,
}) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const retailer = useSelector((state) => state.retailer);

  const formSections = [
    {
      title: 'Add Barcode',
      fields: [
        {
          label: "Barcode",
          name: "code",
          type: "text",
          value: formData?.code,
          description: "Barcodes can be of variable lengths depending on the ISO format of a given code. The system will treat ending zeroes as wild cards to account for price/weight data.",
          options: [],
          handleInputChange: (field, value) => {
            setFormData({
              ...formData,
              [field]: value,
            });
          },
        },
        ...(
          beefCutId ? [] : [
            {
              label: 'Beef Cut',
              name: 'beef_product_id',
              type: 'm2o',
              value: formData?.beef_product_id,
              handleInputChange: (value) => {
                setFormData({
                  ...formData,
                  beef_product_id: value,
                });
              },
              config: {
                size: '12',
                translations: [],
                title: 'Beef Cuts',
                url: '/beef_product',
                searchOnly: 'BEEF_CUTS',
                detailsPayload: {
                  fields: '*,translations.*',
                },
                detailsUrl: `/beef_product`,
                detailsResponseHandler: (data) => {
                  const translatedItem = getTranslation(data, { key: 'languages_id', code: 'en-US' })

                  return {
                    id: data?.id,
                    title: translatedItem.name || "",
                    description: translatedItem?.description || "",
                    imageUrl: `${process.env.REACT_APP_API_URL}/assets/${data?.image}?key=brand-portal-image-card-square`,
                    url: `/beef-cuts/${data.id}`,
                    data,
                  };
                },
                payload: {
                  filter: {
                    '_or': [
                      {
                        retailers: {
                          retailer_id: {
                            '_eq': retailer.id
                          }
                        }
                      },
                      {
                        owner: {
                          id: {
                            '_eq': retailer.id
                          }
                        }
                      }
                    ]
                  },
                  fields: '*, translations.*',
                  limit: 25,
                  meta: '*',
                },
                rows: [],
                responseHandler: (data) => {
                  return data?.data?.map((item) => {
                    const translatedItem = getTranslation(item, { key: 'languages_id', code: 'en-US' })
                    return {
                      id: item?.id,
                      title: translatedItem.name || "",
                      description: translatedItem?.description || "",
                      imageUrl: `${process.env.REACT_APP_API_URL}/assets/${item?.image}`,
                      url: `/beef-cuts/${item.id}`,
                      data: item,
                    };
                  }) || [];
                },
                headCells: [],
                items: [],
              },
            },
          ]
        ),
        {
          label: "URL",
          name: "url",
          type: "text",
          value: formData?.url,
          description: 'A URL override which will always take precedence.  If you put a URL here it will become the scanning destination.',
          options: [],
          handleInputChange: (field, value) => {
            setFormData({
              ...formData,
              [field]: value,
            });
          },
        },
      ],
    },
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const barcodeResponse = await API.addBarcode(
        {
          code: formData?.code,
          url: formData?.url,
          owner: retailer.id,
          beef_product_id: beefCutId || formData?.beef_product_id,
        },
        savedData?.id
      );

      notify("success", "Barcode Created Successfully");
      navigate(`/barcodes/${barcodeResponse?.id}`);
    } catch (err) {
      err?.map((err) => notify("error", err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={(e) => handleFormSubmit(e)}>
      <>
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Grid container>
            <Grid item xs={12} lg={7}>
              {formSections.map((section) => (
                <Box key={section.title}>
                  {section.title && (
                    <Typography
                      className="form-section-title"
                      variant="h4"
                    >
                      {section.title}
                    </Typography>
                  )}

                  <Grid container spacing={2}>
                    {section.fields.map((field) => (
                      <Fragment key={field.name}>
                        <Grid
                          key={field.name}
                          item
                          {...(["colour"].includes(field?.type)
                            ? {
                                xs: 12,
                                md: 4,
                              }
                            : {
                                xs: 12,
                              })}
                        >
                          {chooseInputComponent(field)}
                        </Grid>
                      </Fragment>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} lg={7}>
              <Button
                disabled={loading || !formData?.code || (!formData.beef_product_id && !beefCutId)}
                className={(loading || !formData?.code || (!formData.beef_product_id && !beefCutId)) ? '' : "form-button"}
                sx={{
                  width: "40%",
                  my: 5,
                }}
                size="large"
                variant="contained"
                onClick={handleFormSubmit}
              >
                {loading ? "Saving... " : "Save Barcode"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </>
    </form>
  );
};

export default BarcodeForm;
